/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Helpers} from '../../../helpers';
import {ToastrService} from 'ngx-toastr';
import {RemovalPointService} from '../../../_services/removal-point.service';
import {RemovalPoint} from '../../../_models/removal-point';
import {PHFromString, PhoneNumber} from '../../../_models/phone-number';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {DEFAULT, EMBEDDED} from '../../../_enums/serializer-groups';
import {I18nService, ONE_PHONE_NUMBER_NEEDED, UPDATE_SUCCESS} from '../../../_services/i18n.service';
import {ID, PATTERN, REQUIRED} from '../../../_enums/constants';
import {hasValidation, NO} from '../../../_enums/form-validation';

@Component({
  selector: 'app-input-phone-numbers',
  templateUrl: './input-phone-numbers.component.html',
  styleUrls: ['./input-phone-numbers.component.scss']
})
export class InputPhoneNumbersComponent implements OnInit, OnChanges {

  @ViewChild('phonesChipList', { static: true }) chipList;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             BASE COMPONENT INPUTS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() index?: number;
  @Input() label?: string;
  @Input() showLabel?: boolean;
  @Input() showRequired?: boolean;
  @Input() showValidation?: string;
  @Input() submitted?: boolean;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             CUSTOM COMPONENT INPUTS                                                                                ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() allowAdd?: boolean;
  @Input() allowRemove?: boolean;
  @Input() mobilePhones?: boolean;
  @Input() needData?: boolean;
  @Input() removalPoint: RemovalPoint;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             COMPONENT OUTPUTS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Output() updated = new EventEmitter();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public addOnBlur = true;
  public data: PhoneNumber[] = [];
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private removalPointService: RemovalPointService, private i18n: I18nService, private toastr: ToastrService) {
    this.index = 0;
    this.label = null;
    this.showValidation = NO;
    this.showLabel = true;
    this.showRequired = true;
    this.submitted = false;

    this.allowAdd = false;
    this.allowRemove = false;
    this.mobilePhones = false;
    this.needData = false;
  }

  ngOnInit() {
    this.updateData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.chipList.errorState = hasValidation(this.showValidation) && this.needData && this.submitted ? REQUIRED : null;

    this.updateData();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get canDeletePhoneNumber(): boolean {
    return (this.removalPoint && this.removalPoint.mobilePhones.length + this.removalPoint.phones.length) > 1;
  }

  get isRequired(): boolean {
    return this.chipList.errorState === REQUIRED;
  }

  get notMatchPattern(): boolean {
    return this.chipList.errorState === PATTERN;
  }

  get phones(): PhoneNumber[] {
    if (this.mobilePhones) {
      return (this.removalPoint && this.removalPoint.mobilePhones) ? this.removalPoint.mobilePhones : [];
    } else {
      return (this.removalPoint && this.removalPoint.phones) ? this.removalPoint.phones : [];
    }
  }

  get controlId(): string {
    if (this.removalPoint) {
      return this.mobilePhones ? `mobilePhones_${this.removalPoint.id}_${this.index}_${ID}` : `phones_${this.removalPoint.id}_${this.index}_${ID}`;
    }

    return `phones_${this.index}_${ID}`;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CHANGE LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      const phoneNumberRegex = /^[+][\d]{11,13}$/gi;

      // Check number is valid
      if (phoneNumberRegex.test(value)) {
        this.chipList.errorState = null;

        if (this.removalPoint && this.removalPoint.id) {
          this.addPhoneNumbers([value]);
        } else {
          this.addPhoneNumberNoRemote(PHFromString(value, this.mobilePhones));
        }
      } else {
        this.chipList.errorState = PATTERN;
      }
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  public remove(phoneNumber: PhoneNumber): void {
    if (phoneNumber) {
      if (this.removalPoint && this.removalPoint.id) {
        if (this.canDeletePhoneNumber) {
          this.removePhoneNumbers([phoneNumber.number]);
        } else {
          this.toastr.warning(this.i18n.get(ONE_PHONE_NUMBER_NEEDED));
        }
      } else {
        this.removePhoneNumberNoRemote(phoneNumber);
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ASYNC REQUESTS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private addPhoneNumbers(phoneNumbers: string[]) {
    if (this.removalPoint) {
      Helpers.setLoading(true);

      this.removalPointService.addPhoneNumbers(this.removalPoint.id, phoneNumbers, {
        version: 0.3,
        serializerGroups: [DEFAULT, EMBEDDED]
      }).subscribe(
        (res: HttpResponse<RemovalPoint>) => this.handleResponse(res.body),
        (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  private removePhoneNumbers(phoneNumbers: string[]) {
    if (this.removalPoint) {
      Helpers.setLoading(true);

      this.removalPointService.removePhoneNumbers(this.removalPoint.id, phoneNumbers, {
        version: 0.3,
        serializerGroups: [DEFAULT, EMBEDDED]
      }).subscribe(
        (res: HttpResponse<RemovalPoint>) => this.handleResponse(res.body),
        (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  private handleResponse(rp: RemovalPoint): void {
    Helpers.setLoading(false);
    this.removalPoint = rp;
    this.updateData();
    this.toastr.success(this.i18n.get(UPDATE_SUCCESS));
    this.updated.emit(this.removalPoint);
  }

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

  private updateData(): void {
    this.data = [];
    this.data = this.data.concat(this.phones);
  }

  private addPhoneNumberNoRemote(p: PhoneNumber): void {
    this.phones.push(p);
    this.updateData();
    this.updated.emit(this.removalPoint);
  }

  private removePhoneNumberNoRemote(p: PhoneNumber): void {
    const i = this.data.indexOf(p);

    if (0 >= i) {
      delete this.phones[i];
    }

    this.updateData();
    this.updated.emit(this.removalPoint);
  }

}
